import React, { useState } from 'react';
import { Search, TrendingUp, TrendingDown, Minus, ArrowUpDown } from 'lucide-react';

const KeywordPerformanceTable = ({ keywords }) => {
  const [sortConfig, setSortConfig] = useState({ key: 'position', direction: 'asc' });
  const [searchTerm, setSearchTerm] = useState('');

  const getTrendIcon = (trend) => {
    if (trend === 'up') return <TrendingUp className="text-green-600" size={16} />;
    if (trend === 'down') return <TrendingDown className="text-red-600" size={16} />;
    return <Minus className="text-gray-400" size={16} />;
  };

  const getDifficultyColor = (difficulty) => {
    if (difficulty >= 60) return 'bg-red-100 text-red-800';
    if (difficulty >= 40) return 'bg-orange-100 text-orange-800';
    return 'bg-green-100 text-green-800';
  };

  const getPositionColor = (position) => {
    if (position <= 3) return 'bg-green-100 text-green-800 font-bold';
    if (position <= 10) return 'bg-blue-100 text-blue-800';
    if (position <= 20) return 'bg-orange-100 text-orange-800';
    return 'bg-gray-100 text-gray-800';
  };

  const handleSort = (key) => {
    setSortConfig({
      key,
      direction: sortConfig?.key === key && sortConfig?.direction === 'asc' ? 'desc' : 'asc'
    });
  };

  const filteredKeywords = keywords?.filter(kw => 
    kw?.keyword?.toLowerCase()?.includes(searchTerm?.toLowerCase())
  );

  const sortedKeywords = [...(filteredKeywords || [])]?.sort((a, b) => {
    const aValue = a?.[sortConfig?.key];
    const bValue = b?.[sortConfig?.key];
    
    if (aValue < bValue) return sortConfig?.direction === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortConfig?.direction === 'asc' ? 1 : -1;
    return 0;
  });

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
          <Search className="text-green-600" size={24} />
          Performance des Mots-clés
        </h2>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
          <input
            type="text"
            placeholder="Rechercher un mot-clé..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e?.target?.value)}
            className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
          />
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-200">
              <th className="text-left py-3 px-4 font-semibold text-gray-700">
                <button
                  onClick={() => handleSort('keyword')}
                  className="flex items-center gap-2 hover:text-green-600"
                >
                  Mot-clé
                  <ArrowUpDown size={14} />
                </button>
              </th>
              <th className="text-center py-3 px-4 font-semibold text-gray-700">
                <button
                  onClick={() => handleSort('position')}
                  className="flex items-center gap-2 hover:text-green-600 mx-auto"
                >
                  Position
                  <ArrowUpDown size={14} />
                </button>
              </th>
              <th className="text-center py-3 px-4 font-semibold text-gray-700">
                <button
                  onClick={() => handleSort('volume')}
                  className="flex items-center gap-2 hover:text-green-600 mx-auto"
                >
                  Volume
                  <ArrowUpDown size={14} />
                </button>
              </th>
              <th className="text-center py-3 px-4 font-semibold text-gray-700">
                <button
                  onClick={() => handleSort('difficulty')}
                  className="flex items-center gap-2 hover:text-green-600 mx-auto"
                >
                  Difficulté
                  <ArrowUpDown size={14} />
                </button>
              </th>
              <th className="text-center py-3 px-4 font-semibold text-gray-700">Tendance</th>
            </tr>
          </thead>
          <tbody>
            {sortedKeywords?.map((kw, index) => (
              <tr key={index} className="border-b border-gray-100 hover:bg-gray-50">
                <td className="py-4 px-4">
                  <p className="font-medium text-gray-900">{kw?.keyword}</p>
                </td>
                <td className="py-4 px-4 text-center">
                  <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${getPositionColor(kw?.position)}`}>
                    #{kw?.position}
                  </span>
                </td>
                <td className="py-4 px-4 text-center">
                  <p className="text-gray-900 font-medium">{kw?.volume?.toLocaleString()}</p>
                  <p className="text-xs text-gray-500">recherches/mois</p>
                </td>
                <td className="py-4 px-4 text-center">
                  <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${getDifficultyColor(kw?.difficulty)}`}>
                    {kw?.difficulty}/100
                  </span>
                </td>
                <td className="py-4 px-4 text-center">
                  <div className="flex justify-center">
                    {getTrendIcon(kw?.trend)}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {sortedKeywords?.length === 0 && (
        <div className="text-center py-8">
          <p className="text-gray-500">Aucun mot-clé trouvé</p>
        </div>
      )}
    </div>
  );
};

export default KeywordPerformanceTable;